import { takeLatest, call, put, select } from 'redux-saga/effects';
import { userPageActions } from './index';
import { API_ENDPOINTS } from 'utils/configs';
import fetchClient from 'utils/requestInterceptor';
import {
  displayError,
  displaySuccessToaster,
} from 'app/components/ToasterBar/helpers';
import { UserToasterMsg } from '../../../components/ToasterBar/constants';
import { paginationActions } from '../../../components/Pagination/slice';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  ActivateUserAction,
  DeactivateUserAction,
  DeleteUserAction,
  UpdateUserStatusAction,
} from './types';
import { selectPaginationByType } from '../../../components/Pagination/slice/selectors';

export function* deleteUser(action: PayloadAction<DeleteUserAction>) {
  try {
    const { userId } = action.payload;
    const url = `${API_ENDPOINTS.USERS}/${userId}`;
    yield call(fetchClient.delete, url);
    yield put(userPageActions.deleteUserSucceeded());
    yield call(displaySuccessToaster, UserToasterMsg.DELETE_USER_SUCCEED);

    // Refresh the user list
    yield put(
      paginationActions.initPageData({
        filters: '(email|first_name|last_name)@=*',
        sorts: '',
        page: 0,
        page_size: 12,
        path: API_ENDPOINTS.USERS,
        pagiType: 'pagiUser',
      }),
    );
  } catch (err) {
    yield call(displayError, err);
    yield put(userPageActions.deleteUserFailed());
  }
}

export function* activateUser(action: PayloadAction<ActivateUserAction>) {
  try {
    const { userId } = action.payload;
    const url = `${API_ENDPOINTS.USERS}/${userId}/deactivate?status=${false}`;
    yield call(fetchClient.post, url);
    yield put(userPageActions.activateUserSucceeded());
    yield call(displaySuccessToaster, UserToasterMsg.ACTIVATE_USER_SUCCEED);

    // Update the user's status in the pagination state
    yield put(
      userPageActions.updateUserStatus({
        userId,
        status: 'active',
      }),
    );
  } catch (err) {
    yield call(displayError, err);
    yield put(userPageActions.activateUserFailed());
  }
}

export function* deactivateUser(action: PayloadAction<DeactivateUserAction>) {
  try {
    const { userId } = action.payload;
    const url = `${API_ENDPOINTS.USERS}/${userId}/deactivate?status=${true}`;
    yield call(fetchClient.post, url);
    yield put(userPageActions.deactivateUserSucceeded());
    yield call(displaySuccessToaster, UserToasterMsg.DEACTIVATE_USER_SUCCEED);

    // Update the user's status in the pagination state
    yield put(
      userPageActions.updateUserStatus({
        userId,
        status: 'inactive',
      }),
    );
  } catch (err) {
    yield call(displayError, err);
    yield put(userPageActions.deactivateUserFailed());
  }
}

export function* updateUserStatus(
  action: PayloadAction<UpdateUserStatusAction>,
) {
  const { userId, status } = action.payload;
  const paginationState = yield select(selectPaginationByType('pagiUser'));
  const currentPage = paginationState.cur_page;
  const currentContent = paginationState.content[currentPage];

  if (currentContent?.data) {
    const updatedData = currentContent.data.map(user =>
      user.id === userId ? { ...user, status } : user,
    );

    yield put(
      paginationActions.updatePageData({
        pagiType: 'pagiUser',
        current_page: currentPage,
        contents: updatedData,
        total_count: paginationState.total_count,
        total_pages: paginationState.total_pages,
        page_size: paginationState.page_size,
      }),
    );
  }
}

export function* userPageSaga() {
  yield takeLatest(userPageActions.deleteUser.type, deleteUser);
  yield takeLatest(userPageActions.activateUser.type, activateUser);
  yield takeLatest(userPageActions.deactivateUser.type, deactivateUser);
  yield takeLatest(userPageActions.updateUserStatus.type, updateUserStatus);
}
